import { useEffect, useState, type FunctionComponent } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import size from 'lodash/size';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useUniversalNavigationContext } from '@fff-web/fff-global-nav';
import { showErrorToast, showSuccessToast } from '@fff-web/fff-ui-shared';
import { growthSdk } from '@fff-web/growth-sdk';
import Layout from '../../components/Layout';
import useScreenMode from '../../hooks/useScreenMode';
import { invalidateMemberCaches } from '../../services/member-caches';
import viewSizeTypes from '../../utils/viewSizeTypes';
import { SUBSCRIPTION_STATUS } from '../constants';
import MyAccountNav from './MyAccountNav/MyAccountNav';
import Profile from './Profile/Profile';
import MyAccountRoutes from './routes/MyAccountRoutes';
import { fetchUserAvatarRequest, fetchUserAvatarSuccess } from './services/actions/userActions';
import { getRemainingBoxes, getUserCredits, setUserAvatar } from './services/api/account';
import { getBillingInfo } from './services/api/billing';
import { VIEW_SIZE } from './utils/device';
import envValues from './utils/env';
import { getMembershipType } from './utils/user';

const CLUB_MEMBER = 'club';
/** The box that *is* the membership (vs. add-on boxes like beauty/pup). */
const CORE_BOX_SALE_TYPE = 'seasonal_customize_box';
const ACTIVE_SUBSCRIPTION_FLAGS = [
  'has_box_subscription',
  'has_annual_box_subscription',
  'has_seasonal_box_subscription',
  'has_refill_subscription',
  'has_shopping_subscription_membership',
  'trial',
];

// SPA growth SDK instance (spa=true → manual redirect + credentials included), mirroring the
// ClubMembershipModal precedent. Used to read the core box's next renewal off growth /next-renewals.
const growthSdkInstance = growthSdk(envValues.appEnv as Parameters<typeof growthSdk>[0], true);

export type SubscriptionStatus = keyof typeof SUBSCRIPTION_STATUS;

type MyAccountPageProps = {
  firstName: string;
  lastName: string;
  shippingAddress: Record<string, unknown>;
  avatarUrl: string;
  updateAvatar: (userAvatarUrl: string) => void;
  phone: string;
  email: string;
  activationDate: string;
  getUserAvatar: () => void;
  fetchingUserStatus: boolean;
  userStatusError: boolean;
  userRole: string;
};

const MyAccountPage: FunctionComponent<MyAccountPageProps> = ({
  firstName,
  lastName,
  shippingAddress,
  avatarUrl,
  updateAvatar,
  phone,
  email,
  activationDate,
  getUserAvatar,
  fetchingUserStatus,
  userStatusError,
  userRole,
}) => {
  const {
    membershipInfo,
    userSubscriptions: {
      clubMembershipSubscription,
      boxSubscriptions,
      memberSince,
      refetch: refetchSubscriptions,
    },
  } = useUniversalNavigationContext();
  // Subscription type is derived from the membership endpoint (tier + flags) instead of the legacy
  // `/accounts` `subscription`/`planCode` fields.
  const { isAnnual, subscriptionPlan } = getMembershipType(membershipInfo);
  const isClubMember = userRole === CLUB_MEMBER;
  // Keep legacy My Account aligned with Asado's navbar:
  // active club MEMBERSHIP startAt -> account activationDate -> active membership flag
  // effective_from.
  const subscriptionActiveSince = membershipInfo?.flags?.find((flag) =>
    ACTIVE_SUBSCRIPTION_FLAGS.includes(flag.display_name),
  )?.effective_from;
  const memberSinceDate =
    memberSince !== undefined
      ? (memberSince ?? '')
      : activationDate || subscriptionActiveSince || '';
  const membershipState = membershipInfo?.membership_state;
  // A non-club member's cancel state comes from the core box, not `membership_state` — an active
  // refill/SWB keeps the latter 'active' after the box is cancelled. `nonrenew` only means cancelled
  // while the box is still active; a lapsed box keeps `nonrenew` but goes inactive/EXPIRED. Mirrors
  // asado's getBoxSubscriptionState, pending the maui migration to the BFF `rawSubscriptionState`.
  const coreBoxSubscription = boxSubscriptions?.find(
    (sub) => sub.customizeSaleType === CORE_BOX_SALE_TYPE,
  );
  const coreBoxState =
    coreBoxSubscription &&
    (coreBoxSubscription.active && coreBoxSubscription.nonrenew
      ? SUBSCRIPTION_STATUS.CANCELED
      : coreBoxSubscription.status);
  const rawStatus = isClubMember
    ? (clubMembershipSubscription?.status ?? membershipState)
    : (coreBoxState ?? membershipState);
  const subscriptionState = rawStatus?.toUpperCase() as SubscriptionStatus;

  const { myAccountRemainingBoxes: myAccountRemainingBoxesFF, chatbotAppliedLab: showChatbot } =
    useFlags();
  const screenMode = useScreenMode();
  const [credits, setCredits] = useState<
    {
      amount: number;
      creditName: string;
    }[]
  >([]);
  const [billingInfo, setBillingInfo] = useState({});
  const [remainingBoxes, setRemainingBoxes] = useState<
    {
      orderState: string;
      season: string;
    }[]
  >([]);
  const [nextRenewal, setNextRenewal] = useState<{
    year: number;
    season: string;
    renewalDate: string | Date;
  }>({ year: 0, season: '', renewalDate: '' });
  const viewSize = viewSizeTypes[screenMode as keyof typeof viewSizeTypes];
  const isDesktop = viewSize === VIEW_SIZE.large || viewSize === VIEW_SIZE.xLarge;
  const displayRemainingBoxes = isAnnual && myAccountRemainingBoxesFF && !!size(remainingBoxes);

  const nextRenewalInfo = isClubMember
    ? {
        renewalDate:
          subscriptionState === 'CANCELED' ? '' : (clubMembershipSubscription?.nextBillDate ?? ''),
        season: '',
        year: 0,
      }
    : nextRenewal;

  useEffect(() => {
    const fetchCredits = async () => {
      const fetchedCredits = await getUserCredits();
      setCredits(fetchedCredits);
    };
    const fetchBillingInfo = async () => {
      const response = await getBillingInfo();
      setBillingInfo({ ...response, postalCode: response.zip });
    };
    const fetchUserNextRenewal = async () => {
      const response = await growthSdkInstance.getNextRenewals();
      if (!response.ok) return;
      // Core box is a strict match: type BOX + seasonal_customize_box. A null sale type is a broken
      // payload — let it fall through to the empty state rather than null-tolerating it.
      const coreBox = response.data.renewals.find(
        (renewal) => renewal.type === 'BOX' && renewal.customizeSaleType === CORE_BOX_SALE_TYPE,
      );
      if (coreBox) {
        // Mirror the navbar BFF's season-first precedence: box renewals must display "Next Billing
        // Season". Profile's branch is date-first (`renewalDate || isClubMember`), so when season+year
        // are present we drop any hard renewalDate — otherwise a core box that carries a date (the
        // type allows it for trial/npf==0 boxes) would flip a box member to "Next Billing Date". Falls
        // back to the date only when no season is present.
        const hasSeason = Boolean(coreBox.season && coreBox.year);
        setNextRenewal({
          year: coreBox.year ? Number(coreBox.year) : 0,
          season: coreBox.season ?? '',
          renewalDate: hasSeason ? '' : (coreBox.renewalDate ?? ''),
        });
      }
    };
    fetchCredits();
    fetchBillingInfo();
    if (!avatarUrl) getUserAvatar();
    if (subscriptionState !== SUBSCRIPTION_STATUS.EXPIRED && !isClubMember) fetchUserNextRenewal();
  }, [isClubMember]);

  useEffect(() => {
    const fetchRemainingBoxes = async () => {
      const items = await getRemainingBoxes();
      if (Array.isArray(items)) {
        setRemainingBoxes(items);
      }
    };
    if (isAnnual) {
      fetchRemainingBoxes();
    }
  }, [isAnnual]);

  const uploadMyAvatar = async (formData: FormData) => {
    try {
      const { userAvatarUrl } = await setUserAvatar(formData);
      updateAvatar(userAvatarUrl);
      invalidateMemberCaches();
      showSuccessToast(
        'Updated Profile Photo',
        'Your profile photo change has been successfully saved.',
      );
    } catch (_) {
      showErrorToast(
        'Profile Picture Upload Failed',
        'Your image did not meet our requirements. Please upload a PNG or JPG of at least 300x300 that is under 500KB.',
      );
    }
  };

  // The box list is a cancellation surface, so it must show only *cancellable* boxes:
  // `active` and NOT `nonrenew`. `active` alone is insufficient — a non-renewing box is still
  // active until the end of its cycle but is already cancelled, so it isn't cancellable again.
  // Seasonal customize boxes are the membership itself (cancelled via the membership flow), so
  // they're excluded here regardless of state.
  const filteredBoxSubs = boxSubscriptions?.filter(
    (sub) => sub.active && !sub.nonrenew && sub.customizeSaleType !== CORE_BOX_SALE_TYPE,
  );

  return (
    <Layout isLoading={userStatusError || fetchingUserStatus} hideChatWidget={!showChatbot}>
      <div className={`pandora ${viewSize}`} data-testid="general-container">
        <Profile
          isDesktop={isDesktop}
          firstName={firstName}
          lastName={lastName}
          activationDate={memberSinceDate}
          subscriptionPlan={subscriptionPlan}
          subscriptionState={subscriptionState}
          avatarUrl={avatarUrl}
          uploadAvatar={uploadMyAvatar}
          remainingBoxes={remainingBoxes}
          displayRemainingBoxes={displayRemainingBoxes}
          nextRenewal={nextRenewalInfo}
          membershipInfo={membershipInfo}
          isClubMember={isClubMember}
        />
        {isDesktop && <MyAccountNav />}
        <MyAccountRoutes
          firstName={firstName}
          lastName={lastName}
          shippingAddress={shippingAddress}
          phone={phone}
          email={email}
          credits={credits}
          billingInfo={billingInfo}
          setBillingInfo={setBillingInfo}
          isDesktop={isDesktop}
          isClubMember={isClubMember}
          clubMemberSubscriptionId={clubMembershipSubscription?.id}
          subscriptionState={subscriptionState}
          refetchClubMemberSubscription={refetchSubscriptions}
          boxSubscriptions={filteredBoxSubs}
        />
      </div>
    </Layout>
  );
};

const mapStateToProps = (state) => {
  const { userState, viewState } = state;
  return {
    viewSize: viewState.viewSize,
    firstName: userState.firstName,
    lastName: userState.lastName,
    shippingAddress: userState.shippingAddress,
    userId: userState.userId,
    userRole: userState.role,
    avatarUrl: userState.userAvatarUrl,
    uploadingImageError: userState.error,
    phone: userState.phoneNumber,
    email: userState.email,
    activationDate: userState.activationDate,
    fetchingUserStatus: userState.fetchingUserStatus,
    userStatusError: userState.userStatusError,
  };
};

export default compose(
  connect(mapStateToProps, {
    getUserAvatar: fetchUserAvatarRequest,
    updateAvatar: fetchUserAvatarSuccess,
  }),
)(MyAccountPage);
